import { useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServiceAdmin, serviceAdminAction } from "./use-service-admin.ts";
import { StatusBadge } from "../../../components/index.ts";
import { formatDateTime, isoAttr } from "../../../lib/datetime.ts";

/**
 * The VCI credential explorer, migrated from the VCI service's server-rendered `/admin` UI into the
 * control plane (replaces the standalone VCI admin panel). It reads the VCI admin API through the
 * BFF proxy (`/api/v1/service-admin/vci/credentials`) and renders:
 *   - a dashboard strip: total issued + a per-type breakdown;
 *   - a credential LIST (newest first) with a type filter;
 *   - a credential DETAIL "debugger": metadata, the credentialSubject grid, the proof section, and
 *     the raw JSON — plus the suspend / reinstate / revoke lifecycle actions.
 *
 * Every value comes from the service's own store; nothing is fabricated. Honest empty/loading/error
 * states throughout. Theme-aware via the kit tokens.
 */

/** One stored credential entry as the VCI admin API returns it. */
interface VciEntry {
  /** Stable credential id (urn:uuid:...) the durable store is keyed by. */
  readonly credentialId: string;
  /** In-memory cache display key, present only while the body is still cached. */
  readonly id?: number;
  readonly type: string;
  readonly subject: string | null;
  readonly issuanceDate: string | null;
  readonly expirationDate?: string | null;
  /** Lifecycle status from the durable store (issued/suspended/revoked). */
  readonly status?: string | null;
  /** Effective validity also accounts for a governing organization credential. */
  readonly valid?: boolean;
  readonly organizationCredentialId?: string | null;
  readonly organizationCredentialStatus?: string | null;
  readonly invalidReason?: string | null;
  /** Present only when the in-memory body cache still holds this credential. */
  readonly format?: string;
  readonly credential?: Record<string, unknown>;
}

interface CredentialsResponse {
  readonly credentials: readonly VciEntry[];
}

const CREDENTIAL_TYPES = [
  "AgentCredential",
  "HumanSponsorCredential",
  "OrganizationSponsorCredential",
  "OperationalBoundaryCredential",
] as const;

export function VciCredentials(props: { view?: "dashboard" | "credentials" } = {}): ReactNode {
  if (props.view === "dashboard") return <VciDashboard />;
  return <Credentials />;
}

/**
 * The VCI dashboard, mirroring the VCI service's `/admin` dashboard and the IDP dashboard styling: a
 * stat strip, a Server configuration card (static Port/URL + the live Signing Key ID), and a
 * Credentials card (Total Issued + per-type breakdown). Issued/by-type are live over the existing
 * `/admin/api/credentials` proxy read; the Signing Key ID is not exposed under the admin proxy, so
 * it is shown as "not exposed" rather than implying a value the control plane cannot read.
 */
interface VciConfigResponse {
  readonly port?: number;
  readonly url?: string;
  readonly signing_key_id?: string;
}

function VciDashboard(): ReactNode {
  const listQuery = useServiceAdmin<CredentialsResponse>("vci", "credentials", ["credentials", ""]);
  const configQuery = useServiceAdmin<VciConfigResponse>("vci", "config", ["config"]);
  const entries = listQuery.data?.credentials ?? [];
  const byType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of entries) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    return counts;
  }, [entries]);
  const presentTypes = useMemo(
    () => CREDENTIAL_TYPES.filter((t) => (byType.get(t) ?? 0) > 0),
    [byType],
  );

  if (listQuery.isError) {
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load the dashboard</strong>
        <span>The VCI admin service could not be reached. {listQuery.error.message}</span>
      </div>
    );
  }

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-adm2__stats" aria-label="VCI summary">
        <StatTile label="Port" value="443" />
        <StatTile label="Total Issued" value={listQuery.isPending ? "…" : String(entries.length)} />
      </div>

      <div className="svc-adm2__grid">
        <section className="svc-adm2__card" aria-label="Server configuration">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Server configuration</h3>
          </div>
          <dl className="svc-adm2__kv">
            <div className="svc-admin__kv-row"><dt>Port</dt><dd>443</dd></div>
            <div className="svc-admin__kv-row">
              <dt>URL</dt>
              <dd>{configQuery.data?.url ?? "https://vci.oathera.org"}</dd>
            </div>
            <div className="svc-admin__kv-row">
              <dt>Signing Key ID</dt>
              <dd>
                {configQuery.isPending ? (
                  <span className="svc-admin__muted">Loading…</span>
                ) : configQuery.data?.signing_key_id ? (
                  <code className="svc-adm2__mono" style={{ wordBreak: "break-all" }}>{configQuery.data.signing_key_id}</code>
                ) : (
                  <span className="svc-admin__muted">Unavailable</span>
                )}
              </dd>
            </div>
          </dl>
        </section>

        <section className="svc-adm2__card" aria-label="Credentials">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Credentials</h3>
            {!listQuery.isPending && <span className="svc-adm2__card-sub">{entries.length} issued</span>}
          </div>
          <div className="svc-admin__section">
            <h4 className="svc-admin__section-title">Total Issued</h4>
            <p className="svc-admin__stat-value" style={{ fontSize: "20px" }}>{listQuery.isPending ? "…" : entries.length}</p>
          </div>
          <dl className="svc-adm2__kv">
            <div className="svc-admin__kv-row svc-admin__kv-row--head"><dt>By Type</dt><dd></dd></div>
            {listQuery.isPending ? (
              <div className="svc-admin__kv-row"><dt><span className="svc-admin__muted">Loading…</span></dt><dd></dd></div>
            ) : presentTypes.length === 0 ? (
              <div className="svc-admin__kv-row"><dt><span className="svc-admin__muted">No credentials issued yet.</span></dt><dd></dd></div>
            ) : (
              presentTypes.map((t) => (
                <div key={t} className="svc-admin__kv-row">
                  <dt>{shortType(t)}</dt>
                  <dd>{byType.get(t) ?? 0}</dd>
                </div>
              ))
            )}
          </dl>
        </section>
      </div>
    </div>
  );
}

function Credentials(): ReactNode {
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useServiceAdmin<CredentialsResponse>(
    "vci",
    `credentials${typeFilter ? `?type=${encodeURIComponent(typeFilter)}` : ""}`,
    ["credentials", typeFilter],
  );

  const entries = listQuery.data?.credentials ?? [];

  // Per-type breakdown for the dashboard strip, computed from the full (unfiltered) set when no
  // filter is active, else from the filtered set (which is what the service returned).
  const breakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of entries) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    return counts;
  }, [entries]);

  const selected = selectedId === null ? null : entries.find((e) => e.credentialId === selectedId) ?? null;

  if (listQuery.isError) {
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load credentials</strong>
        <span>The VCI admin service could not be reached. {listQuery.error.message}</span>
      </div>
    );
  }

  return (
    <div className="svc-admin svc-admin--full">
      {/* Dashboard strip: total + per-type counts. */}
      <div className="svc-adm2__stats" aria-label="Credential statistics">
        <StatTile label="Issued" value={listQuery.isPending ? "…" : String(entries.length)} />
        {CREDENTIAL_TYPES.map((t) => (
          <StatTile key={t} label={shortType(t)} value={String(breakdown.get(t) ?? 0)} />
        ))}
      </div>

      <div className="svc-admin__split">
        {/* LIST */}
        <section className="svc-admin__list" aria-label="Issued credentials">
          <div className="svc-admin__list-head">
            <h3 className="svc-admin__h">Credentials</h3>
            <label className="svc-admin__filter">
              <span className="sr-only">Filter by type</span>
              <select
                className="control__select"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setSelectedId(null);
                }}
              >
                <option value="">All types</option>
                {CREDENTIAL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {shortType(t)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {listQuery.isPending ? (
            <div className="state-box" data-kind="loading" role="status">
              <strong>Loading…</strong>
              <span>Fetching issued credentials.</span>
            </div>
          ) : entries.length === 0 ? (
            <div className="state-box" data-kind="empty">
              <strong>No credentials</strong>
              <span>
                {typeFilter
                  ? `No ${shortType(typeFilter)} credentials have been issued.`
                  : "No credentials have been issued yet."}
              </span>
            </div>
          ) : (
            <ul className="svc-admin__rows">
              {entries.map((e) => {
                const lifecycleStatus = (e.status ?? "issued").toLowerCase();
                const displayStatus = lifecycleStatus === "issued" && e.valid === false ? "invalid" : lifecycleStatus;
                return (
                <li key={e.credentialId}>
                  <button
                    type="button"
                    className="svc-admin__row"
                    data-active={e.credentialId === selectedId ? "true" : undefined}
                    onClick={() => setSelectedId(e.credentialId)}
                  >
                    <StatusBadge
                      level={displayStatus === "revoked" || displayStatus === "invalid" ? "error" : displayStatus === "suspended" ? "warn" : "healthy"}
                      label={displayStatus === "revoked" ? "Revoked" : displayStatus === "suspended" ? "Suspended" : displayStatus === "invalid" ? "Invalid" : "Issued"}
                      size="sm"
                    />
                    <span className="svc-admin__row-id">#{e.id ?? e.credentialId.slice(-8)}</span>
                    <span className="svc-admin__row-type">{shortType(e.type)}</span>
                    <span className="svc-admin__row-subject">{e.subject ?? "—"}</span>
                    {e.issuanceDate !== null && (
                      <time className="svc-admin__row-time" dateTime={isoAttr(Date.parse(e.issuanceDate))}>
                        {safeDate(e.issuanceDate)}
                      </time>
                    )}
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* DETAIL */}
        <section className="svc-admin__detail" aria-label="Credential detail">
          {selected === null ? (
            <div className="state-box" data-kind="empty">
              <strong>Select a credential</strong>
              <span>Choose a credential from the list to inspect its fields.</span>
            </div>
          ) : (
            <VciCredentialDetail entry={selected} typeFilter={typeFilter} />
          )}
        </section>
      </div>
    </div>
  );
}

function VciCredentialDetail(props: { entry: VciEntry; typeFilter: string }): ReactNode {
  const { entry } = props;
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The LIST entry carries metadata only. Fetch the DETAIL endpoint to get the
  // full body: the server now returns the signed VC from the DURABLE store, so
  // issuer/format/claims survive a service restart. Legacy records (issued before
  // the body was persisted, with an empty cache after restart) come back without
  // a `credential` body — then we honestly degrade.
  const detailQuery = useServiceAdmin<{ credential: VciEntry }>(
    "vci",
    `credentials/${encodeURIComponent(entry.credentialId)}`,
    ["credential", entry.credentialId],
  );
  const detail = detailQuery.data?.credential;
  const cred = detail?.credential ?? entry.credential;
  const format = detail?.format ?? entry.format;
  const subject = (cred?.["credentialSubject"] ?? {}) as Record<string, unknown>;
  const proof = (cred?.["proof"] ?? {}) as Record<string, unknown>;

  const act = async (action: "suspend" | "reinstate" | "revoke"): Promise<void> => {
    setBusy(action);
    setError(null);
    try {
      await serviceAdminAction(`vci`, `credentials/${encodeURIComponent(entry.credentialId)}/${action}`);
      await qc.invalidateQueries({ queryKey: ["service-admin", "vci"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "The action could not be completed.");
    } finally {
      setBusy(null);
    }
  };

  // Durable lifecycle vocabulary: issued | suspended | revoked. Effective validity additionally
  // fails closed when the exact governing organization credential is no longer active.
  const lifecycleStatus = (detail?.status ?? entry.status ?? "issued").toLowerCase();
  const effectiveValid = detail?.valid ?? entry.valid;
  const displayStatus = lifecycleStatus === "issued" && effectiveValid === false ? "invalid" : lifecycleStatus;
  const displayId = entry.id ?? entry.credentialId.slice(-8);

  return (
    <div className="svc-admin__cred">
      <header className="svc-admin__cred-head">
        <h3 className="svc-admin__h">
          Credential #{displayId}
          <span className="svc-admin__cred-type">{shortType(entry.type)}</span>
        </h3>
        <StatusBadge
          level={displayStatus === "revoked" || displayStatus === "invalid" ? "error" : displayStatus === "suspended" ? "warn" : "healthy"}
          label={displayStatus === "revoked" ? "Revoked" : displayStatus === "suspended" ? "Suspended" : displayStatus === "invalid" ? "Invalid" : "Issued"}
          size="sm"
        />
      </header>

      {/* Lifecycle actions. Disable each when its precondition is unmet under the
          durable transition rules: Suspend only from issued, Reinstate only from
          suspended, Revoke from anything except already-revoked (terminal). */}
      <div className="svc-admin__actions" role="group" aria-label="Credential actions">
        <button type="button" className="svc-admin__btn" disabled={busy !== null || lifecycleStatus !== "issued"} onClick={() => void act("suspend")}>
          {busy === "suspend" ? "Suspending…" : "Suspend"}
        </button>
        <button type="button" className="svc-admin__btn" disabled={busy !== null || lifecycleStatus !== "suspended"} onClick={() => void act("reinstate")}>
          {busy === "reinstate" ? "Reinstating…" : "Reinstate"}
        </button>
        <button type="button" className="svc-admin__btn svc-admin__btn--danger" disabled={busy !== null || lifecycleStatus === "revoked"} onClick={() => void act("revoke")}>
          {busy === "revoke" ? "Revoking…" : "Revoke"}
        </button>
      </div>
      {error !== null && (
        <p className="svc-admin__err" role="alert">
          {error}
        </p>
      )}

      {/* Metadata. */}
      <KeyGrid
        title="Metadata"
        rows={[
          ["Identifier", entry.credentialId],
          ["Type", entry.type],
          ["Format", format ?? "—"],
          ["Issuer", cred ? String(cred["issuer"] ?? "—") : "—"],
          ["Issued", entry.issuanceDate ? safeDate(entry.issuanceDate) : "—"],
          ["Effective validity", effectiveValid === undefined ? "—" : effectiveValid ? "Valid" : "Invalid"],
          ["Organization credential", detail?.organizationCredentialId ?? entry.organizationCredentialId ?? "—"],
          ["Organization status", detail?.organizationCredentialStatus ?? entry.organizationCredentialStatus ?? "—"],
          ["Invalid reason", detail?.invalidReason ?? entry.invalidReason ?? "—"],
        ]}
      />

      {cred ? (
        <>
          {/* credentialSubject grid. */}
          <KeyGrid title="Subject" rows={Object.entries(subject).map(([k, v]) => [k, stringify(v)])} />

          {/* proof section. */}
          <KeyGrid
            title="Proof"
            rows={["type", "created", "verificationMethod", "proofPurpose", "proofValue"].map((k) => [
              k,
              stringify(proof[k]),
            ])}
          />

          {/* Raw JSON. */}
          <details className="svc-admin__raw">
            <summary>Raw credential JSON</summary>
            <pre>{JSON.stringify(cred, null, 2)}</pre>
          </details>
        </>
      ) : detailQuery.isPending ? (
        <section className="svc-admin__section">
          <p className="svc-admin__muted">Loading credential body…</p>
        </section>
      ) : (
        <section className="svc-admin__section">
          <p className="svc-admin__muted">
            The full credential body is not available (the service restarted before it was persisted
            durably). Metadata and lifecycle status above come from the durable store.
          </p>
        </section>
      )}
    </div>
  );
}

function StatTile(props: { label: string; value: string }): ReactNode {
  return (
    <div className="svc-admin__stat">
      <span className="svc-admin__stat-value">{props.value}</span>
      <span className="svc-admin__stat-label">{props.label}</span>
    </div>
  );
}

function KeyGrid(props: { title: string; rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <section className="svc-admin__section">
      <h4 className="svc-admin__section-title">{props.title}</h4>
      {props.rows.length === 0 ? (
        <p className="svc-admin__muted">No fields.</p>
      ) : (
        <dl className="svc-admin__kv">
          {props.rows.map(([k, v]) => (
            <div key={k} className="svc-admin__kv-row">
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Shorten a credential type for compact display ("AgentCredential" → "Agent"). */
function shortType(t: string): string {
  return t.replace(/Credential$/, "") || t;
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function safeDate(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? formatDateTime(ms) : iso;
}
